import { useState } from 'react';
import {
  Button,
  Flex,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useTheme,
} from '@chakra-ui/react';
import { SocialConnectedProfile, SocialProfile } from 'src/api/v1-api';
import MobileRowLabel from '../mobile-row-label';
import { getSocialProfileIcon } from 'src/utils/socialProfileIcons';
import { MdAddCircle, MdEdit, MdMoreVert, MdOutlineAutoMode, MdRemoveCircle } from 'react-icons/md';
import { FaTrash } from 'react-icons/fa';
import { SUPPORTED_AYRSHARE_SOCIAL_NETWORKS } from 'src/utils/marketplaceUtils';
import { useMarketplaceContext } from 'src/contexts/marketplace';
import { FEATURE_IDS } from 'src/constants/tier-features';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import UpsellTierModal from 'src/components/atoms/upsell-tier-modal';

interface SocialProfilesTableProps {
  socialProfiles: SocialProfile[];
  connectedSocialProfiles: SocialConnectedProfile[];
  editSocialProfile: (socialProfile: Partial<SocialProfile>) => void;
  onUpdateSocialProfile: (socialProfile: SocialProfile) => void;
  onDeleteSocialProfile: (socialProfile: SocialProfile) => void;
}

const AUTOPILOT_HELP_TEXT =
  'In order to enable Loma Autopilot features for any of your social accounts, we need you to directly connect your account(s). Establishing this initial connection won’t create any automation, but will allow you to enable different Autopilot features afterward. Contact Support with questions.';

const SocialProfilesTable = ({
  socialProfiles,
  connectedSocialProfiles,
  editSocialProfile,
  onUpdateSocialProfile,
  onDeleteSocialProfile,
}: SocialProfilesTableProps) => {
  const [showAutoPilotToolTip, setShowAutoPilotToolTip] = useState<string | null>(null);
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
  const { marketplace } = useMarketplaceContext();
  const theme = useTheme();

  const isSocialProfileConnected = (socialProfile: SocialProfile): boolean => {
    const connectedProfile = connectedSocialProfiles.find(
      (profile) =>
        (profile.profileUrl &&
          socialProfile.url &&
          profile.profileUrl.toLowerCase() ===
            socialProfile.url.replace(/\/$/, '').toLowerCase()) ||
        (profile.platform === socialProfile.network &&
          ((profile.username &&
            socialProfile.username &&
            profile.username.toLowerCase() ===
              socialProfile.username.replace(/^@/, '').toLowerCase()) ||
            (profile.pageName &&
              socialProfile.username &&
              profile.pageName.toLowerCase() ===
                socialProfile.username.replace(/^@/, '').toLowerCase()))),
    );
    return !!connectedProfile;
  };

  const isSocialAutomationLocked =
    marketplace?.tier?.features[FEATURE_IDS.SOCIAL_AUTOMATION] === false;

  const handleConnectAutopilotClick = (e: React.MouseEvent) => {
    if (isSocialAutomationLocked) {
      e.preventDefault();
      setIsUpsellModalOpen(true);
    }
  };

  return (
    <Flex direction="column" width="100%" className="social-profiles-table">
      <Flex
        py={4}
        w="100%"
        borderBottom="1px solid"
        borderColor="gray.200"
        gap={4}
        display={{ base: 'none', lg: 'flex' }}
        color="gray.600"
        textTransform="uppercase"
        fontSize="xs"
        fontWeight="bold"
      >
        <Flex w="15%" pl={4}>
          Network
        </Flex>
        <Flex w="25%">Username</Flex>
        <Flex w="55%">Link</Flex>
        <Flex w="5%" justifyContent="end" pr={4}>
          Actions
        </Flex>
      </Flex>
      {socialProfiles.map((profile, index) => (
        <Flex
          key={profile.id}
          flexDirection={{ base: 'column', lg: 'row' }}
          w="100%"
          borderBottom="1px solid"
          borderColor="gray.200"
          py={{ base: 6, lg: 2 }}
          gap={4}
          alignItems={{ base: 'flex-start', lg: 'center' }}
        >
          <Flex w={{ lg: '15%' }} pl={{ base: 0, lg: 4 }} direction="column">
            <MobileRowLabel label="Network" />
            {getSocialProfileIcon({
              networkName: profile.network,
              fillColor: theme.colors.mainText,
            })}
          </Flex>
          <Flex w={{ lg: '25%' }} direction="column">
            <MobileRowLabel label="Username" />
            <Text noOfLines={1}>{profile.username}</Text>
          </Flex>
          <Flex w={{ lg: '55%' }} direction="column">
            <MobileRowLabel label="Link" />
            <Text noOfLines={1}>
              <Link href={profile.url} target="_blank">
                {profile.url}
              </Link>
            </Text>
          </Flex>
          <Flex display={{ base: 'flex', lg: 'none' }} direction="row" gap={2} flexWrap={'wrap'}>
            <Button
              size="sm"
              colorScheme="gray"
              onClick={() => editSocialProfile(profile)}
              leftIcon={<MdEdit />}
            >
              Edit
            </Button>
            <Button
              size="sm"
              colorScheme="gray"
              onClick={() => onDeleteSocialProfile(profile)}
              leftIcon={<FaTrash />}
            >
              Remove
            </Button>
            <Button
              size="sm"
              colorScheme="gray"
              onClick={() =>
                onUpdateSocialProfile({
                  ...profile,
                  show_on_header_menu: !profile.show_on_header_menu,
                })
              }
              leftIcon={profile.show_on_header_menu ? <MdRemoveCircle /> : <MdAddCircle />}
            >
              {profile.show_on_header_menu ? 'Remove from' : 'Add to'} header
            </Button>
            <Button
              size="sm"
              colorScheme="gray"
              onClick={() =>
                onUpdateSocialProfile({
                  ...profile,
                  show_on_footer_menu: !profile.show_on_footer_menu,
                })
              }
              leftIcon={profile.show_on_footer_menu ? <MdRemoveCircle /> : <MdAddCircle />}
            >
              {profile.show_on_footer_menu ? 'Remove from' : 'Add to'} footer
            </Button>
            <Flex alignItems={'center'} gap={3}>
              {SUPPORTED_AYRSHARE_SOCIAL_NETWORKS.includes(profile.network) &&
                (isSocialAutomationLocked ? (
                  <Button
                    colorScheme={isSocialProfileConnected(profile) ? 'green' : 'gray'}
                    size="small"
                    p="2"
                    leftIcon={<MdOutlineAutoMode />}
                    onClick={handleConnectAutopilotClick}
                    opacity={0.6}
                    cursor="pointer"
                    _hover={{
                      background: 'gray.100',
                    }}
                  >
                    {isSocialProfileConnected(profile)
                      ? 'Autopilot Connected'
                      : 'Connect Autopilot'}
                  </Button>
                ) : (
                  <>
                    <Button
                      as={Link}
                      href={`/api/marketplace/${marketplace?.id}/manage_social_connected_accounts`}
                      target="_blank"
                      colorScheme={isSocialProfileConnected(profile) ? 'green' : 'gray'}
                      size="small"
                      p="2"
                      leftIcon={<MdOutlineAutoMode />}
                    >
                      {isSocialProfileConnected(profile)
                        ? 'Autopilot Connected'
                        : 'Connect Autopilot'}
                    </Button>
                    <Tooltip
                      hasArrow
                      isOpen={showAutoPilotToolTip === `mobile-${index}`}
                      label={AUTOPILOT_HELP_TEXT}
                      bg="gray.100"
                      color="black"
                    >
                      <InfoOutlineIcon
                        onMouseEnter={() => setShowAutoPilotToolTip(`mobile-${index}`)}
                        onMouseLeave={() => setShowAutoPilotToolTip(null)}
                      />
                    </Tooltip>
                  </>
                ))}
            </Flex>
          </Flex>
          <Flex w="5%" justifyContent="end" pr={4} display={{ base: 'none', lg: 'flex' }}>
            <Menu>
              <MenuButton as={IconButton} aria-label="Actions" icon={<MdMoreVert />} />
              <MenuList>
                <MenuItem icon={<MdEdit />} onClick={() => editSocialProfile(profile)}>
                  Edit
                </MenuItem>
                <MenuItem icon={<FaTrash />} onClick={() => onDeleteSocialProfile(profile)}>
                  Remove
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    onUpdateSocialProfile({
                      ...profile,
                      show_on_header_menu: !profile.show_on_header_menu,
                    })
                  }
                  icon={profile.show_on_header_menu ? <MdRemoveCircle /> : <MdAddCircle />}
                >
                  {profile.show_on_header_menu ? 'Remove from' : 'Add to'} header
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    onUpdateSocialProfile({
                      ...profile,
                      show_on_footer_menu: !profile.show_on_footer_menu,
                    })
                  }
                  icon={profile.show_on_footer_menu ? <MdRemoveCircle /> : <MdAddCircle />}
                >
                  {profile.show_on_footer_menu ? 'Remove from' : 'Add to'} footer
                </MenuItem>
                {SUPPORTED_AYRSHARE_SOCIAL_NETWORKS.includes(profile.network) &&
                  (isSocialAutomationLocked ? (
                    <MenuItem
                      onMouseEnter={() => setShowAutoPilotToolTip(`desktop-${index}`)}
                      onMouseLeave={() => setShowAutoPilotToolTip(null)}
                      background={isSocialProfileConnected(profile) ? 'green.200' : ''}
                      icon={<MdOutlineAutoMode />}
                      onClick={handleConnectAutopilotClick}
                      opacity={0.6}
                      cursor="pointer"
                      _hover={{
                        background: 'gray.100',
                      }}
                    >
                      <Flex alignItems="center">
                        {isSocialProfileConnected(profile)
                          ? 'Autopilot Connected'
                          : 'Connect Autopilot'}
                      </Flex>
                    </MenuItem>
                  ) : (
                    <MenuItem
                      as={Link}
                      href={`/api/marketplace/${marketplace?.id}/manage_social_connected_accounts`}
                      target="_blank"
                      onMouseEnter={() => setShowAutoPilotToolTip(`desktop-${index}`)}
                      onMouseLeave={() => setShowAutoPilotToolTip(null)}
                      background={isSocialProfileConnected(profile) ? 'green.200' : ''}
                      icon={<MdOutlineAutoMode />}
                    >
                      <Flex alignItems="center">
                        {isSocialProfileConnected(profile)
                          ? 'Autopilot Connected'
                          : 'Connect Autopilot'}
                        <Tooltip
                          hasArrow
                          isOpen={showAutoPilotToolTip === `desktop-${index}`}
                          label={AUTOPILOT_HELP_TEXT}
                          bg="gray.100"
                          color="black"
                        >
                          <InfoOutlineIcon ml={4} />
                        </Tooltip>
                      </Flex>
                    </MenuItem>
                  ))}
              </MenuList>
            </Menu>
          </Flex>
        </Flex>
      ))}
      <UpsellTierModal
        isOpen={isUpsellModalOpen}
        onClose={() => setIsUpsellModalOpen(false)}
        featureId={FEATURE_IDS.SOCIAL_AUTOMATION}
      />
    </Flex>
  );
};

export default SocialProfilesTable;
